import axios, { AxiosResponse } from 'axios'
import React, { useEffect, useState } from 'react'
import {
  createBurnOrder,
  createMintOrder,
  CreateOrderDTO,
  OrderModel,
  UpdateBurnDTO,
  updateBurnOrder
} from 'src/common/api'
import { ChainAsset, ChainName, FiatCurrency, RequestType, StagingChainAsset } from 'src/common/static'
import { alertNotification } from 'src/ui'
import { useAuth, useBlockchain } from 'src/common/context'
import { WalletModel } from 'src/common/api'
import { logEvent } from '../../analytics/analytics'

interface Errors {
  inputError?: string
  walletError?: string
}

interface Order extends OrderModel {
  asset: ChainAsset
  chain: ChainName
}

const useRequest = (type: RequestType) => {
  const isMint = type === RequestType.MINT
  const isBurn = type === RequestType.BURN

  const { wallets } = useBlockchain()
  const { user } = useAuth()
  const [value, setValue] = useState('')
  const [asset, setAsset] = useState(ChainAsset.QCAD)
  const [selectedChain, setSelectedChain] = useState<ChainName>(ChainName.ETHEREUM)
  const [fiatCurrency, setFiatCurrency] = useState(FiatCurrency.CAD)

  // Select first MINTING wallet that matches the selected chain, or first MINTING wallet if none match
  const selectedWallet: WalletModel = wallets.find((w) => w.chain === selectedChain && w.walletType === 'MINTING') ||
    wallets.find((w) => w.walletType === 'MINTING') ||
    wallets.find((w) => w.chain === selectedChain) ||
    wallets[0] || {
      id: '',
      address: '',
      name: '',
      chain: selectedChain
    }
  const selectedWalletBalance = selectedWallet?.balance
  const qcadWalletBalance = selectedWalletBalance?.qcad ?? selectedWalletBalance?.qcad_xlm ?? 0
  const isOptedIn = true // Blockchain details are always "opted in" (they exist)

  const [modals, setModals] = useState({
    choosePayment: false,
    billpay: false,
    eft: false,
    interac: false,
    confirm: false,
    requestLoading: false,
    kycPortal: false,
    bankDetails: false
  })
  const [showPaymentInstructions, setShowPaymentInstructions] = useState(false)
  const [showBurnInstructions, setShowBurnInstructions] = useState(false)
  const [networkFee, setNetworkFee] = useState(0)
  const [errors, setErrors] = useState<Errors>({
    inputError: undefined,
    walletError: undefined
  })
  const [loading, setLoading] = useState(false)
  const [order, setOrder] = useState<Order | null>(null)
  const [createdBurnOrder, setCreatedBurnOrder] = useState<OrderModel | null>(null)

  const [anchorElAsset, setAnchorElAsset] = useState<null | HTMLElement>(null)
  const [anchorElChain, setAnchorElChain] = useState<null | HTMLElement>(null)
  const [anchorElFiatCurrency, setAnchorElFiatCurrency] = useState<null | HTMLElement>(null)

  useEffect(() => {
    setAsset(ChainAsset.QCAD)
  }, [selectedWallet.chain])

  const handleRequest = async () => {
    let inputError
    let walletError
    if (Number(value) === 0) {
      inputError = 'Must be greater than 0'
    }
    if (!selectedWallet.address) {
      walletError = 'Please choose a wallet first'
    }
    if (!(inputError || walletError)) {
      setLoading(true)
      const start = performance.now()
      try {
        if (isMint) {
          // For mint, go directly to payment instructions
          setNetworkFee(0) // Currently network fee is 0
          setShowPaymentInstructions(true)
          logEvent('button_click', { buttonName: 'Mint Order Button' })
        } else if (isBurn) {
          // For burn, create the order first (to set up listener), then show instructions
          const response = await createOrder()
          // Store the created order but don't set it yet (to show instructions first)
          setCreatedBurnOrder(response.data)
          setShowBurnInstructions(true)
          logEvent('button_click', { buttonName: 'Burn Order Button' })
          const processingTime = (performance.now() - start) / 1000
          logEvent('order_processing_time', { orderId: response.data.id, processingTime_s: processingTime })
        }
        setLoading(false)
      } catch (err) {
        if (axios.isAxiosError(err)) {
          alertNotification("Something went wrong and your order couldn't be processed.")
        } else {
          alertNotification((err as Error).message)
        }
        setLoading(false)
      }
      setErrors({ inputError, walletError })
    }
  }

  const createOrder = async (): Promise<AxiosResponse<OrderModel>> => {
    try {
      if (!user?.organization_id) {
        throw new Error('Organization not found')
      }
      let asset: ChainAsset | StagingChainAsset
      if (
        process.env.REACT_APP_API_URL?.includes('staging') ||
        process.env.REACT_APP_API_URL?.includes('sandbox') ||
        process.env.REACT_APP_API_URL?.includes('localhost')
      ) {
        asset = StagingChainAsset.QCAD_TEST
      } else {
        asset = ChainAsset.QCAD
      }
      const orderRequestBody: CreateOrderDTO = {
        asset,
        fiatAmount: Number(value),
        fiatCurrency,
        cryptoAmount: Number(value),
        address: selectedWallet.address as string,
        chain: selectedChain,
        organizationId: user.organization_id
      }

      console.log(orderRequestBody)

      const response = isMint ? await createMintOrder(orderRequestBody) : await createBurnOrder(orderRequestBody)
      return response
    } catch (err) {
      throw new Error('Something went wrong. Please try again later.')
    }
  }

  const handlePaymentInstructionsProceed = async () => {
    const start = performance.now()
    setShowPaymentInstructions(false)
    handleOpen('requestLoading')
    logEvent('button_click', { buttonName: 'Proceed Button - Mint Payment Instructions' })
    try {
      const response = await createOrder()
      setOrder({ ...response.data, chain: selectedChain, asset })
      const processingTime = (performance.now() - start) / 1000
      logEvent('order_processing_time', { orderId: response.data.id, processingTime_s: processingTime })
    } catch (err) {
      if (axios.isAxiosError(err)) {
        alertNotification("Something went wrong and your order couldn't be processed.")
      } else {
        alertNotification((err as Error).message)
      }
    }
    handleClose('requestLoading')
  }

  const handleBurnInstructionsProceed = async () => {
    setShowBurnInstructions(false)
    // Set the order that was created in handleRequest
    logEvent('button_click', { buttonName: 'Proceed Button - Burn Return Address Instructions' })
    if (createdBurnOrder) {
      setOrder({ ...createdBurnOrder, chain: selectedChain, asset })
      setCreatedBurnOrder(null)
    }
  }

  const handleConfirmRequest = async (stopLoading: () => void) => {
    if (isMint) {
      handleClose('confirm')
      handleOpen('requestLoading')
    }
    try {
      let response = await createOrder()
      if (isBurn) {
        handleClose('confirm')
        handleOpen('requestLoading')

        const orderRequestBody: UpdateBurnDTO = {
          orderId: response.data.id
        }
        response = await updateBurnOrder(orderRequestBody)
      }
      setOrder({ ...response.data, chain: selectedChain, asset })
    } catch (err) {
      if (axios.isAxiosError(err)) {
        alertNotification("Something went wrong and your order couldn't be processed.")
      } else {
        alertNotification((err as Error).message)
      }
    }
    handleClose('requestLoading')
    stopLoading()
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value
    if (Number(newValue) > 0 && errors.inputError) {
      setErrors((s) => ({ ...s, inputError: undefined }))
    }

    if (isBurn && asset === ChainAsset.QCAD) {
      if (
        process.env.REACT_APP_API_URL?.includes('staging') ||
        process.env.REACT_APP_API_URL?.includes('sandbox') ||
        process.env.REACT_APP_API_URL?.includes('localhost')
      ) {
        setErrors((s) => ({ ...s, inputError: undefined }))
      } else {
        if (Number(newValue) > qcadWalletBalance) {
          setErrors((s) => ({ ...s, inputError: 'Insufficient QCAD balance' }))
        }
      }
    }
    setValue(newValue)
  }

  const handleClose = (modal: string) => {
    setModals((s) => ({ ...s, [modal]: false }))
  }

  const handleOpen = (modal: string) => {
    setModals((s) => ({ ...s, [modal]: true }))
  }

  const handleOpenAssetMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElAsset(event.currentTarget)
  }

  const handleCloseAssetMenu = () => {
    setAnchorElAsset(null)
  }

  const chooseAsset = (asset: ChainAsset) => {
    setAsset(asset)
    setValue('')
    setErrors((s) => ({ ...s, inputError: undefined }))
    handleCloseAssetMenu()
  }

  const handleOpenFiatCurrencyMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElFiatCurrency(event.currentTarget)
  }

  const handleCloseFiatCurrencyMenu = () => {
    setAnchorElFiatCurrency(null)
  }

  const chooseFiatCurrency = (fiatCurrency: FiatCurrency) => {
    setFiatCurrency(fiatCurrency)
    setValue('')
    setErrors((s) => ({ ...s, inputError: undefined }))
    handleCloseFiatCurrencyMenu()
  }

  const handleOpenChainMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElChain(event.currentTarget)
  }

  const handleCloseChainMenu = () => {
    setAnchorElChain(null)
  }

  const chooseChain = (chain: ChainName) => {
    setSelectedChain(chain)
    setValue('')
    setErrors((s) => ({ ...s, inputError: undefined }))
    handleCloseChainMenu()
  }

  return {
    value,
    modals,
    asset,
    selectedChain,
    order,
    loading,
    errors,
    anchorElAsset,
    anchorElChain,
    fiatCurrency,
    anchorElFiatCurrency,
    networkFee,
    showPaymentInstructions,
    showBurnInstructions,
    handleRequest,
    handlePaymentInstructionsProceed,
    handleBurnInstructionsProceed,
    handleConfirmRequest,
    handleChange,
    handleClose,
    handleOpenAssetMenu,
    handleCloseAssetMenu,
    chooseAsset,
    handleOpenChainMenu,
    handleCloseChainMenu,
    chooseChain,
    handleOpenFiatCurrencyMenu,
    handleCloseFiatCurrencyMenu,
    chooseFiatCurrency,
    selectedWallet,
    selectedWalletBalance,
    isOptedIn
  }
}

export default useRequest
